// The battle stage's ground (client/boardview.ts): a small patch of a biome painted with the hunt map's terrain
// painter (client/terrain.ts), its grass and flowers, trees and rocks along the far edge and the sides, the cells
// cut into the ground and the centre line. Painted once per biome and size at source-pixel scale, then drawn scaled up
// with no smoothing, so it reads as the same pixel art as the hunt. A boss fight plays in the boss's own lair instead: a
// picture painted for that boss and stage size (art/rig3d/build-lair.ts) with the same cells and centre line cut in.
import { BIOMES, T_GROUND, tileRand, type WorldMap, type MapProp } from '../shared/worldmap.ts';
import { paintTerrain } from './terrain.ts';
import { artInfo, has, sprite, spriteSize } from './assets.ts';

/** A prop with its base at (px, ground), as the hunt map stamps it (client/field.ts stampProp). */
function stampProp(x: CanvasRenderingContext2D, id: string, px: number, ground: number) {
  const img = sprite(id), a = artInfo(id);
  if (a) { x.drawImage(img, 0, 0, a.w, a.h, Math.round(px - a.w / 2), Math.round(ground - (a.h - a.foot)), a.w, a.h); return; }
  const sz = spriteSize(id);
  x.drawImage(img, Math.round(px - sz / 2), Math.round(ground - sz));
}
/** Ground decor into the terrain canvas (client/field.ts paintDecor); false while sprites load. */
function paintDecor(m: WorldMap, c: HTMLCanvasElement): boolean {
  const x = c.getContext('2d')!;
  let ok = true;
  for (const p of m.props) {
    if (p.block || !has(p.id)) continue;
    if (!loaded(p.id)) { ok = false; continue; }
    stampProp(x, p.id, p.x * TILE, p.y * TILE + 4);
  }
  return ok;
}

const TILE = 24;
/** A cell as the ground shows it: centre in source pixels, and whose side. */
export interface SceneCell { x: number; y: number; mine: boolean }
interface Scene { key: string; c: HTMLCanvasElement; ready: boolean; tries: number; waiting?: string[] }
const scenes = new Map<string, Scene>();

/** Each boss's lair (scene `lair-<boss>`): the biome its weather comes from, and its ground while it has no painted lair. */
export const LAIR_BIOME: Record<string, string> = { fenrir: 'glacier', typhon: 'ash', hachimata: 'underworld' };

/** Biomes per backdrop: a ranked league, a Trail region, a boss's lair, or the plain stage. */
export const SCENE_BIOME: Record<string, string> = {
  plain: 'ruins', bronze: 'ruins', silver: 'crag', gold: 'fields', platinum: 'glacier', mythic: 'shrine', legend: 'ember',
  midgard: 'snow', hellas: 'ruins', yomi: 'shrine', tournament: 'village',
  ...Object.fromEntries(Object.entries(LAIR_BIOME).map(([boss, biome]) => [`lair-${boss}`, biome])),
};

const loaded = (id: string): boolean => { const i = sprite(id); return !(i instanceof HTMLImageElement) || (i.complete && i.naturalWidth > 0); };

/** Ellipse rim in whole pixels (rx, ry in pixels). */
function pixelEllipse(x: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry: number, color: string, fill?: string) {
  if (fill) {
    x.fillStyle = fill;
    for (let dy = -ry; dy <= ry; dy++) { const w = Math.round(rx * Math.sqrt(1 - (dy * dy) / (ry * ry))); x.fillRect(Math.round(cx - w), Math.round(cy + dy), w * 2, 1); }
  }
  x.fillStyle = color;
  const n = Math.round((rx + ry) * 3.2);
  for (let i = 0; i < n; i++) { const a = (i / n) * Math.PI * 2; x.fillRect(Math.round(cx + Math.cos(a) * rx), Math.round(cy + Math.sin(a) * ry), 1, 1); }
}

/** The centre line (a row of pale stones) and the cells (a flat slab ringed in gilt, yours, or rust, theirs). */
function cutIn(x: CanvasRenderingContext2D, cells: readonly SceneCell[], centreX: number, ah: number) {
  for (let y = Math.round(ah * 0.24); y < ah * 0.97; y += 7) {
    x.fillStyle = 'rgba(20,12,8,.55)'; x.fillRect(Math.round(centreX) - 1, y, 3, 3);
    x.fillStyle = 'rgba(236,214,160,.7)'; x.fillRect(Math.round(centreX) - 1, y, 2, 2);
  }
  for (const cell of cells) {
    pixelEllipse(x, cell.x, cell.y + 1, 17, 7, 'rgba(0,0,0,.35)', 'rgba(16,10,6,.22)');
    pixelEllipse(x, cell.x, cell.y, 17, 7, cell.mine ? 'rgba(244,206,120,.85)' : 'rgba(226,108,84,.85)');
    pixelEllipse(x, cell.x, cell.y, 12, 4, cell.mine ? 'rgba(244,206,120,.3)' : 'rgba(226,108,84,.3)');
  }
}

/**
 * A boss's lair for `scene` (`lair-<boss>`) at aw × ah source pixels, with `cells` cut in: undefined when the scene is no
 * lair or the boss has no painted lair (the caller paints the biome ground), null while the picture loads.
 */
export function lairGround(scene: string | undefined, aw: number, ah: number, cells: readonly SceneCell[], centreX: number): HTMLCanvasElement | null | undefined {
  if (!scene?.startsWith('lair-')) return undefined;
  const id = `${scene}-${aw > ah ? 'wide' : 'tall'}`;
  if (!has(id)) return undefined;
  if (!loaded(id)) return null;
  const key = `${id}:${cells.length}`;
  let s = scenes.get(key);
  if (s) return s.c;
  const c = document.createElement('canvas');
  c.width = aw; c.height = ah;
  const x = c.getContext('2d')!;
  x.imageSmoothingEnabled = false;
  x.drawImage(sprite(id), 0, 0, aw, ah);
  cutIn(x, cells, centreX, ah);
  s = { key, c, ready: true, tries: 0 };
  scenes.set(key, s);
  return c;
}

/**
 * The ground for `biome` at aw × ah source pixels, with `cells` cut in. Returns null until the first paint; while props
 * load it repaints a few times.
 */
export function arenaGround(biome: string, aw: number, ah: number, cells: readonly SceneCell[], centreX: number): HTMLCanvasElement | null {
  const b = BIOMES[biome] ?? BIOMES.ruins;
  const key = `${b.id}:${aw}x${ah}:${cells.length}`;
  let s = scenes.get(key);
  if (s && (s.ready || s.tries > 40)) return s.c;
  // Painting the ground takes a good 100 ms on a laptop. While props load, repaint only once every one it waited on is
  // in (it used to repaint every frame until then: a run of freezes as a battle opened).
  if (s?.waiting?.some((id) => !loaded(id))) return s.c;
  if (s) s.tries++;
  const w = Math.ceil(aw / TILE) + 1, h = Math.ceil(ah / TILE) + 1;
  const seed = 7151 + b.id.length * 131;
  const props: MapProp[] = [];
  // Ground decor scattered over the field, thinner in the middle where the fight is.
  for (let ty = 0; ty < h; ty++) for (let tx = 0; tx < w; tx++) {
    const r = tileRand(seed, tx, ty, 3);
    const mid = Math.abs(tx * TILE - aw / 2) < aw * 0.3 && ty * TILE > ah * 0.22;
    if (b.decor.length && r < b.decorDensity * (mid ? 0.4 : 1.6)) props.push({ x: tx + 0.2 + tileRand(seed, tx, ty, 6) * 0.6, y: ty + 0.3 + tileRand(seed, tx, ty, 7) * 0.5, id: b.decor[Math.floor(tileRand(seed, tx, ty, 9) * b.decor.length)], block: false });
  }
  const m: WorldMap = {
    key: `arena:${key}`, seed, w, h, biome: b, tiles: new Uint8Array(w * h).fill(T_GROUND), reach: new Uint8Array(w * h).fill(1), open: [],
    start: { x: w / 2, y: h / 2 }, th: { water: -1, ridge: 0 }, props,
  };
  const full = paintTerrain(m);
  const decorOk = paintDecor(m, full);
  const c = document.createElement('canvas');
  c.width = aw; c.height = ah;
  const x = c.getContext('2d')!;
  x.imageSmoothingEnabled = false;
  x.drawImage(full, 0, 0);
  // The fighting ground: a darker trodden oval under the cells.
  x.globalAlpha = 0.16;
  pixelEllipse(x, aw / 2, ah * 0.6, aw * 0.47, ah * 0.36, 'rgba(0,0,0,0)', '#20140c');
  x.globalAlpha = 1;
  cutIn(x, cells, centreX, ah);
  // Trees and rocks along the far edge and down the sides, far ones first.
  const edge: { x: number; y: number; id: string }[] = [];
  const pool = [...b.trees, ...b.trees, ...b.blockers];
  if (pool.length) {
    for (let px = 6; px < aw; px += 22) edge.push({ x: px + tileRand(seed, px, 1, 11) * 10, y: 30 + tileRand(seed, px, 2, 12) * 12, id: pool[Math.floor(tileRand(seed, px, 3, 13) * pool.length)] });
    for (let py = 70; py < ah + 20; py += 40) for (const side of [0, 1]) {
      edge.push({ x: side ? aw - 4 - tileRand(seed, py, side, 14) * 8 : 4 + tileRand(seed, py, side, 14) * 8, y: py, id: b.blockers.length ? b.blockers[Math.floor(tileRand(seed, py, side, 15) * b.blockers.length)] : pool[0] });
    }
  }
  edge.sort((p, q) => p.y - q.y);
  let propsOk = true;
  for (const p of edge) {
    if (!has(p.id)) continue;
    if (!loaded(p.id)) { propsOk = false; continue; }
    stampProp(x, p.id, p.x, p.y);
  }
  // A soft shade toward the far edge, so the trees sit back.
  const fade = x.createLinearGradient(0, 0, 0, ah * 0.3);
  fade.addColorStop(0, 'rgba(10,6,4,.45)'); fade.addColorStop(1, 'rgba(10,6,4,0)');
  x.fillStyle = fade; x.fillRect(0, 0, aw, ah * 0.3);
  const waiting = [...new Set([...props, ...edge].map((p) => p.id).filter((id) => has(id) && !loaded(id)))];
  s = { key, c, ready: decorOk && propsOk, tries: (s?.tries ?? 0) + 1, waiting };
  scenes.set(key, s);
  return c;
}
